import type React from 'react';
import { useContext } from 'react';

import { FaTimes } from 'react-icons/fa';
import { ConfigContext } from '../context/ConfigContext';
import type { ChatWindowTitle } from '../types/ChatWindow';
import { Banner } from './Banner';
import FullscreenToggleButton from './FullscreenToggleButton';

type TProps = {
  onClose: (e: React.MouseEvent) => void;
  bannerMessage?: string;
  bannerStatus: 'info' | 'error';
  isFullscreen?: boolean;
  onToggleFullscreen?: () => void;
};

export const ChatTitle: React.FC<TProps> = ({
  onClose,
  bannerMessage,
  bannerStatus,
  isFullscreen,
  onToggleFullscreen,
}) => {
  const getTextContainerType = (props: ChatWindowTitle) => {
    const { title, subtitle, headerTitleColor, headerSubtitleColor } = props;

    if (!subtitle) {
      return (
        <h3 className='header-title' style={{ color: headerTitleColor }}>
          {title}
        </h3>
      );
    }

    return (
      <div className='header-text-container'>
        <h3 className='header-title' style={{ color: headerTitleColor }}>
          {title}
        </h3>
        <h5 className='header-subtitle' style={{ color: headerSubtitleColor }}>
          {subtitle}
        </h5>
      </div>
    );
  };

  const getHeaderByType = (props: ChatWindowTitle) => {
    const {
      title,
      subtitle,
      avatarPath,
      avatarType,
      headerColor,
      headerTitleColor,
      headerType,
      showCloseButton,
    } = props;

    switch (headerType) {
      case 'slim': {
        const actualTitle = subtitle ? `${title} - ${subtitle}` : title;
        return (
          <div
            className='header-container slim'
            style={{ backgroundColor: headerColor }}>
            <div className='header-text-container'>
              <p
                className='header-title slim'
                style={{ color: headerTitleColor }}>
                {actualTitle}
              </p>
            </div>
            <div className='header-actions'>
              {onToggleFullscreen && (
                <FullscreenToggleButton
                  isFullscreen={isFullscreen || false}
                  onToggle={onToggleFullscreen}
                />
              )}
              {showCloseButton && (
                <div className='header-close-button'>
                  <FaTimes onClick={(event) => onClose(event)} />
                </div>
              )}
            </div>
          </div>
        );
      }
      default: {
        return (
          <div
            className='header-container normal'
            style={{ backgroundColor: headerColor }}>
            {headerType === 'normal' && avatarPath && (
              <figure className={`header-avatar ${avatarType}`}>
                <img src={avatarPath} alt='' />
              </figure>
            )}
            {getTextContainerType(props)}
            <div className='header-actions'>
              {onToggleFullscreen && (
                <FullscreenToggleButton
                  isFullscreen={isFullscreen || false}
                  onToggle={onToggleFullscreen}
                />
              )}
              {showCloseButton && (
                <div className='header-close-button'>
                  <FaTimes onClick={(event) => onClose(event)} />
                </div>
              )}
            </div>
          </div>
        );
      }
    }
  };

  const {
    headerConfig: {
      title,
      subtitle,
      logo: avatarPath,
      logoType: avatarType,
      headerType,
    },
    scheme: {
      header: { headerTitleColor, headerColor, headerSubtitleColor },
    },
    greeting: { showCloseButton },
  } = useContext(ConfigContext);

  return (
    <>
      {getHeaderByType({
        title,
        subtitle,
        avatarPath,
        avatarType,
        headerType,
        headerTitleColor,
        headerSubtitleColor,
        headerColor,
        showCloseButton,
      })}
      <Banner bannerMessage={bannerMessage} bannerStatus={bannerStatus} />
    </>
  );
};

export default ChatTitle;
